import { CreditReportAccountOverview } from "types/landlord/reports";
import { Section } from "views/applications-id-report/components/Section";
import moment from "moment";
import { EMPTY_VALUE } from "views/applications-id-report/utils/constants";

const monthsToYearsAndMonths = (months: number) => {
  if (!Number.isFinite(months) || months < 0) return EMPTY_VALUE;
  const years = Math.floor(months / 12);
  const remainingMonths = Math.round(months % 12);
  return `${years} years, ${remainingMonths} months`;
};

const CreditStat = ({
  title,
  value,
  secondary
}: {
  title: string;
  value: string;
  secondary?: string;
}) => {
  return (
    <div className="w-[200px]">
      <div className="text-sm text-content-1000">{title}</div>
      <div className="text-base text-content-1000 my-1 font-bold truncate">
        {value}
      </div>
      {secondary && <div className="text-xs text-content-300">{secondary}</div>}
    </div>
  );
};
export const AccountsSummary = ({
  isNoHit,
  accounts
}: {
  accounts?: CreditReportAccountOverview[];
  isNoHit: boolean;
}) => {
  if (isNoHit)
    return (
      <Section sectionTitle="Accounts summary" maxWidth="100%">
        <p className="text-base font-medium text-content-300">
          No data available
        </p>
      </Section>
    );

  if (!accounts?.length) return null;

  // Age runs from when the account was opened to when it stopped being active, or
  // to today for accounts still open. Bureaus that only report date_opened (e.g.
  // TransUnion Canada) would otherwise collapse every account's age to zero.
  const datedAccounts = accounts.filter(
    (account) => account.date_opened && moment(account.date_opened).isValid()
  );

  const averageAccountAgeMonths =
    datedAccounts.reduce((acc, account) => {
      const closedOrLast = account.date_closed || account.last_activity_date;
      const end =
        closedOrLast && moment(closedOrLast).isValid()
          ? moment(closedOrLast)
          : moment();

      return acc + end.diff(moment(account.date_opened), "months", false);
    }, 0) / datedAccounts.length;

  const oldestAccount = datedAccounts.reduce((acc, account) => {
    if (moment(account.date_opened).isBefore(acc.date_opened)) return account;

    return acc;
  }, datedAccounts[0]);

  const mostRecentAccount = datedAccounts.reduce((acc, account) => {
    if (moment(account.date_opened).isAfter(acc.date_opened)) return account;

    return acc;
  }, datedAccounts[0]);

  const formatOpenedAt = (date?: string | null) =>
    date && moment(date).isValid()
      ? moment(date).format("MMM DD, YYYY")
      : undefined;

  return (
    <Section sectionTitle="Accounts summary" maxWidth="100%">
      {/* Eric asked me not to calculate that */}
      {/* <Stat title="Length of Credit History" /> */}

      <div className="flex gap-x-4 gap-y-8 flex-wrap">
        <CreditStat
          title="Average Account Age"
          value={monthsToYearsAndMonths(averageAccountAgeMonths)}
        />

        <CreditStat
          title="Oldest Account"
          value={oldestAccount?.account_name || EMPTY_VALUE}
          secondary={formatOpenedAt(oldestAccount?.date_opened)}
        />

        <CreditStat
          title="Most Recent Account"
          value={mostRecentAccount?.account_name || EMPTY_VALUE}
          secondary={formatOpenedAt(mostRecentAccount?.date_opened)}
        />

        <CreditStat
          title="Number of accounts"
          value={String(accounts.length)}
        />

        <CreditStat
          title="Revolving"
          value={String(
            accounts.filter((account) => account.portfolio_type?.code === "R")
              .length
          )}
        />

        <CreditStat
          title="Installments"
          value={String(
            accounts.filter((account) => account.portfolio_type?.code === "I")
              .length
          )}
        />

        <CreditStat
          title="Mortgage"
          value={String(
            accounts.filter((account) => account.portfolio_type?.code === "M")
              .length
          )}
        />

        {/* Haven't seen it yet */}
        {/* <CreditStat title="Line of Credit" /> */}

        <CreditStat
          title="Other"
          value={String(
            accounts.filter(
              (account) =>
                !["R", "I", "M"].includes(account.portfolio_type?.code || "")
            ).length
          )}
        />
      </div>
    </Section>
  );
};
